import moment from "moment";
import pdfMake from "../pdfmakeWrapper";
import { getSecureItem } from "../../../utilsFunctions";
import { ImageBaseUrl } from "../../../Environment";
import { getDaysWeeksMonths } from ".";

// Utility: Convert image URL to base64
const getBase64ImageFromUrl = (url) =>
  fetch(url, { mode: "cors" })
    .then((res) => {
      if (!res.ok) throw new Error("Network response was not ok");
      return res.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

const PdfReportArrear = async ({
  sortedData,
  selectedCA,
  date,
  loanType,
  loanTypeData,
  loanTenure,
  companyData,
  cAgentData,
  addEC,
  // extraColumns,
  boxData,
}) => {
  // const extraColumnValues = addEC
  //   ? Object.values(extraColumns).filter((val) => val?.toString().trim() !== "")
  //   : [];

  // const extraColumnCount = extraColumnValues.length;

  const getLabelByValue = (data, value) =>
    data?.find((item) => item.value === value)?.label || null;

  const formatAddress = (addr) =>
    [
      addr?.AddressStreet,
      addr?.AddressLandmark,
      addr?.AddressCity,
      addr?.AddressState,
      addr?.AddressPincode,
    ]
      .filter(Boolean)
      .join(", ");

  const imageUrl =
    getSecureItem("loggedinUserCompany") === "1365"
      ? "https://i.imghippo.com/files/CCD2780qQ.jpeg"
      : `${ImageBaseUrl}${companyData?.profilePic}`;

  try {
    const imageBase64 = await getBase64ImageFromUrl(imageUrl);
    buildAndDownloadPdf(imageBase64);
  } catch (err) {
    console.warn("Image load failed, continuing without logo:", err);
    buildAndDownloadPdf(null);
  }

  function buildAndDownloadPdf(imageBase64) {
    // Header section with logo and company info
    const headerSection = [
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                columns: [
                  imageBase64
                    ? { image: imageBase64, width: 60, height: 70 }
                    : {
                        text: "LOGO",
                        width: 60,
                        height: 70,
                        alignment: "center",
                        color: "#aaa",
                      },
                  {
                    width: "*",
                    alignment: "center",
                    stack: [
                      { text: companyData.companyName, style: "companyName" },
                      {
                        text: `Address: ${formatAddress(companyData.address)}`,
                        style: "infoText",
                      },
                      {
                        text: `GSTIN: ${companyData.GstIn || ""} | Website: ${
                          companyData.companyWebsite || ""
                        }`,
                        style: "infoText",
                      },
                      {
                        text: `Mobile No: ${companyData.phone} | Email ID: ${companyData.companyMail}`,
                        style: "infoText",
                      },
                    ],
                  },
                ],
                columnGap: 10,
              },
            ],
          ],
        },
        layout: { hLineWidth: () => 1, vLineWidth: () => 1 },
        margin: [0, 0, 0, 10],
      },
    ];

    // Filter summary
    const filterSection = [
      { text: "Day-Wise Demand/Arrear Report", style: "title" },
      {
        text: `Collection Agent: ${
          getLabelByValue(cAgentData, selectedCA) || "All"
        }`,
        style: "filter",
      },
      {
        text: `Loan Type: ${getLabelByValue(loanTypeData, loanType) || "All"}`,
        style: "filter",
      },
      { text: `Sequence: ${loanTenure || "All"}`, style: "filter" },
      {
        text: `Date: ${date ? moment(date).format("LL") : "Life-Time"}`,
        style: "filter",
        margin: [0, 0, 0, 20],
      },
    ].map((item) => ({ ...item, alignment: "center" }));

    const threeBlockRow = {
      table: {
        widths: ["*", "*", "*", "*"],
        body: [
          boxData.map((item) => ({
            stack: [
              { text: item.label, style: "fourBlockHeading" },
              { text: item.value, style: "fourBlockValue" },
            ],
            alignment: "center",
            style: "fourBlock",
          })),
        ],
      },
      layout: { hLineWidth: () => 1, vLineWidth: () => 1 },
      margin: [0, 0, 0, 10],
    };

    const tableHeaders = [
      "Sr No",
      "Loan Id",
      "Name",
      "Area/Line",
      "Collection Agent",
      getSecureItem("loggedinUserCompany") === "3310" ? "Due Date" : "Repayable Amount",
      "Current Installment Due",
      "Amount Collected Till Date",
      "Duration",
      "Paid Till Date",
      "Not-Paid Till Date",
      "Unmarked Till Date",
      "Pending Dues Till Date",
      "Last Payment Date",
      "Outstanding Balance",
      // ...extraColumnValues,
    ];

    // Table rows
    const tableBody = [
      // Header row
      [
        ...tableHeaders.map((header) => ({
          text: header,
          style: "tableHeader",
          alignment: "center",
        })),
      ],
      ...sortedData.map((data, index) => {
        const borrowerName = `${data?.customer?.firstName || ""} ${data?.customer?.lastName || ""}`;
        const custId = data?.customer?.cust_id ? `(${data.customer.cust_id})` : "";
        const phone = data?.customer?.phone ? `(${data?.customer?.phone})` : "-";

        const baseRow = [
          index + 1,
          `${data.loanId}\n${custId}`,
          `${borrowerName}\n${phone}`,
          data?.customer?.areaLine,
          getLabelByValue(cAgentData, data?.collectorId),
          getSecureItem("loggedinUserCompany") === "3310"
            ? moment(data?.nextDueDate).format("LL")
            : data?.totalAmountToCollect,
          data?.installment,
          data?.collectedAmount,
          getDaysWeeksMonths(data?.loanTenure, data?.loanTenureType),
          data?.paidCln,
          data?.notPaidCln,
          data?.unmarkedCln,
          data?.pendingDuesTD,
          data?.lastCollectedDate ? moment(data?.lastCollectedDate).format("LL") : "",
          data?.outStanding,
        ];

        // return [...baseRow, ...Array(extraColumnCount).fill("")];
        return baseRow;
      }),
    ];

    // ------------------------------------------------------------------
    // COLUMN WIDTH CALCULATION
    // ------------------------------------------------------------------
    // pdfMake adds its own left/right cell padding on TOP of whatever
    // width is specified in `widths`. If that padding isn't accounted
    // for, the physical (padding-inclusive) width of the table ends up
    // bigger than the page, and the right-most columns get silently
    // clipped off the page — which is why "Last Payment Date" and
    // "Outstanding Balance" were disappearing.
    //
    // Fix: explicitly set small, fixed cell padding via a custom layout,
    // then reserve that padding's total width up front before scaling
    // the remaining space across the columns.

    const CELL_PADDING_LEFT = 2;
    const CELL_PADDING_RIGHT = 2;
    const CELL_PADDING_TOP = 3;
    const CELL_PADDING_BOTTOM = 3;

    // Base fixed widths — relative proportions only, will be scaled to fit the page
    const baseWidths = [15, 28, 45, 27, 30, 30, 35, 30, 25, 30, 30, 30, 30, 30, 45];

    // Actual usable width of an A4 landscape page after margins
    const PAGE_WIDTH = 841.89; // A4 landscape, points
    const PAGE_MARGIN = 10;    // matches pageMargins below
    const usableWidth = PAGE_WIDTH - PAGE_MARGIN * 2;

    // Width for each extra (custom) column
    // const extraColumnWidth = 42;
    // const extraWidths = Array(extraColumnCount).fill(extraColumnWidth);

    // const allBaseWidths = [...baseWidths, ...extraWidths];
    const allBaseWidths = baseWidths;
    const totalColumns = allBaseWidths.length;

    // Total horizontal space that will be consumed by cell padding alone
    const totalPaddingOverhead =
      totalColumns * (CELL_PADDING_LEFT + CELL_PADDING_RIGHT);

    // The space actually available for the column "content" widths
    const contentWidth = usableWidth - totalPaddingOverhead;

    const currentTotal = allBaseWidths.reduce((a, b) => a + b, 0);

    // Scale content widths to fill the page width exactly (padding included),
    // regardless of column count
    const scaleFactor = contentWidth / currentTotal;

    const finalWidths = allBaseWidths.map((w) => Math.round(w * scaleFactor));

    // PDF Document Definition
    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [10, 10, 10, 10],
      defaultStyle: {
        alignment: "center",
        fontSize: 9,
      },
      styles: {
        title: { fontSize: 16, bold: true, marginBottom: 10 },
        filter: { fontSize: 10, marginBottom: 2 },
        companyName: {
          fontSize: 14,
          bold: true,
          marginBottom: 4,
          marginTop: 5,
        },
        fourBlockHeading: {
          fontSize: 11,
          bold: true,
          margin: [0, 5, 0, 2],
        },
        fourBlockValue: {
          fontSize: 10,
          margin: [0, 2, 0, 5],
        },
        infoText: { fontSize: 9, lineHeight: 1.3 },
        tableHeader: { fontSize: 10, bold: true },
      },
      content: [
        ...headerSection,
        ...filterSection,
        threeBlockRow,
        {
          table: {
            headerRows: 1,
            widths: finalWidths, // 👈 scaled widths, padding-aware
            body: tableBody,
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? "#f0f0f0" : null),
            paddingLeft: () => CELL_PADDING_LEFT,
            paddingRight: () => CELL_PADDING_RIGHT,
            paddingTop: () => CELL_PADDING_TOP,
            paddingBottom: () => CELL_PADDING_BOTTOM,
          },
        },
      ],
    };

    pdfMake.createPdf(docDefinition).download("DemandReport.pdf");
  }
};

export default PdfReportArrear;
